import React, { useEffect, useRef } from 'react';
import { ADSENSE_CLIENT_ID, ADSENSE_BANNER_SLOT, adsEnabled } from '../../config/ads';

// BannerAd — mobile-only Google AdSense display banner, pinned just above the
// bottom tab bar. Contained in a near-black, hairline-framed slot held clear of
// the nav (design restraint + AdSense accidental-click policy). Renders nothing
// unless AdSense ids are configured, so the default (no-config) build is clean.
//
//   ┌───────────────────────────────┐
//   │  page content (scrolls)       │
//   ├───────────────────────────────┤ ← BannerAd  (fixed, bottom: 72px + safe)
//   ├───────────────────────────────┤ ← BottomTabBar (fixed, bottom: 0, 72px)
//   └───────────────────────────────┘

let scriptInjected = false;

function ensureAdSenseScript() {
  if (scriptInjected || typeof document === 'undefined' || !ADSENSE_CLIENT_ID) return;
  // The loader normally lives in public/index.html <head>; only inject a
  // fallback if no adsbygoogle loader is present, so we never double-load it.
  if (document.querySelector('script[src*="adsbygoogle.js"], script[data-mogged-adsense]')) {
    scriptInjected = true;
    return;
  }
  const s = document.createElement('script');
  s.async = true;
  s.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${ADSENSE_CLIENT_ID}`;
  s.crossOrigin = 'anonymous';
  s.setAttribute('data-mogged-adsense', '');
  document.head.appendChild(s);
  scriptInjected = true;
}

const BannerAd = () => {
  const pushed = useRef(false);

  useEffect(() => {
    if (!adsEnabled() || pushed.current) return;
    ensureAdSenseScript();
    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
      pushed.current = true;
    } catch (_e) {
      // AdSense not ready / blocked by an ad blocker — leave the slot empty,
      // never throw into the render tree.
    }
  }, []);

  if (!adsEnabled()) return null;

  return (
    <div
      className="lg:hidden fixed inset-x-0 z-30 border-t border-hairline-soft bg-obsidian overflow-hidden"
      style={{ bottom: 'calc(72px + env(safe-area-inset-bottom))', height: 52 }}
    >
      <ins
        className="adsbygoogle"
        style={{ display: 'block', width: '100%', height: 52 }}
        data-ad-client={ADSENSE_CLIENT_ID}
        data-ad-slot={ADSENSE_BANNER_SLOT}
        data-ad-format="horizontal"
        data-full-width-responsive="false"
      />
    </div>
  );
};

export default BannerAd;
